import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Medal, Award, Users, Flame, Gift, ArrowRight, TrendingUp, Sparkles } from "lucide-react";
import { adsApi } from "./api";
import ReferralShareCard from "./ReferralShareCard";
import { useLanguage } from "../contexts/LanguageContext";
import "./ads-creator.css";

// Ordered rank styling — top 3 get special colors; rest use monochrome.
// Label keys resolve via translations in component body.
const RANK_THEME = {
  1: { icon: Trophy, glow: "0 0 40px rgba(253,224,71,0.35)",
       bg: "linear-gradient(135deg,#fde047 0%,#f59e0b 100%)", text: "#1f1300", labelKey: "comm_label_champion", prize: 200 },
  2: { icon: Medal, glow: "0 0 28px rgba(203,213,225,0.25)",
       bg: "linear-gradient(135deg,#e2e8f0 0%,#94a3b8 100%)", text: "#0f172a", labelKey: "comm_label_rising", prize: 100 },
  3: { icon: Award, glow: "0 0 26px rgba(251,146,60,0.25)",
       bg: "linear-gradient(135deg,#fdba74 0%,#c2410c 100%)", text: "#2b1500", labelKey: "comm_label_top", prize: 50 },
};

export default function CommunityLeaderboard() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myRef, setMyRef] = useState(null);
  const [hallOfFame, setHallOfFame] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await adsApi.get("/community/top-sharers", { params: { days: 30, limit: 10 } });
        setData(r.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
    (async () => {
      try {
        const r = await adsApi.get("/credits/coupons/my-referral");
        setMyRef(r.data);
      } catch { /* anon — ignore */ }
    })();
    (async () => {
      try {
        const r = await adsApi.get("/community/champions", { params: { limit: 6 } });
        setHallOfFame(r.data.periods || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const top = data?.top || [];
  const totals = data?.totals || { credits: 0, referrals: 0 };

  return (
    <div className="ads-root" data-testid="community-leaderboard"
      style={{
        minHeight: "100vh",
        background: "radial-gradient(60% 50% at 50% 0%, rgba(168,85,247,0.18) 0%, rgba(11,11,18,0) 60%), #0b0b12",
      }}
    >
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "56px 24px 80px" }}>
        <Link to="/ads-creator" style={{ color: "#8b8ba7", fontSize: 13, textDecoration: "none" }}>
          {t.comm_back || "← TopRankPulse Studio"}
        </Link>

        <header style={{ textAlign: "center", marginTop: 28 }}>
          <span
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "6px 14px", borderRadius: 999,
              background: "rgba(168,85,247,0.15)", border: "1px solid rgba(168,85,247,0.35)",
              color: "#c084fc", fontSize: 12, fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase",
            }}>
            <Flame size={12} /> {t.comm_last30 || "Last 30 days"}
          </span>
          <h1 style={{
            color: "#fff", fontSize: "clamp(36px,5.2vw,60px)", fontWeight: 900,
            letterSpacing: "-0.02em", lineHeight: 1.05, margin: "16px 0 10px",
          }}>
            {t.comm_title_a || "Top sharers"}<br />
            <span style={{ background: "linear-gradient(90deg,#a855f7,#7c3aed)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              {t.comm_title_b || "earning free credits"}
            </span>
          </h1>
          <p style={{ color: "#a5a5c0", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.6 }}>
            {t.comm_desc || "Every time someone redeems your personal code, you earn +10 credits. The more you share, the higher you climb."}
          </p>
        </header>

        {/* Monthly prize announcement — crucial motivator */}
        <div
          data-testid="monthly-prize-banner"
          style={{
            marginTop: 28, padding: "18px 20px",
            background: "linear-gradient(135deg,rgba(253,224,71,0.15) 0%,rgba(245,158,11,0.08) 50%,rgba(124,58,237,0.12) 100%)",
            border: "1px solid rgba(253,224,71,0.35)",
            borderRadius: 16,
            display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
          }}>
          <Sparkles size={26} color="#fde047" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ color: "#fde047", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
              🏆 {t.comm_prize_eyebrow || "Monthly prize pool"}
            </div>
            <div style={{ color: "#fff", fontSize: 15, fontWeight: 700, marginTop: 4, lineHeight: 1.4 }}>
              <span style={{ color: "#fde047" }}>+200</span> · <span style={{ color: "#e2e8f0" }}>+100</span> · <span style={{ color: "#fdba74" }}>+50</span> {t.comm_prize_body || "bonus credits for the top 3 every 1st of the month."}
            </div>
          </div>
        </div>

        {/* Community stats bar */}
        <div style={{
          display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12,
          maxWidth: 560, margin: "32px auto 0",
        }}>
          <StatChip
            icon={<TrendingUp size={18} color="#4ade80" />}
            label={t.comm_stat_credits || "Credits earned together"}
            value={`+${(totals.credits || 0).toLocaleString()}`}
            testid="leaderboard-total-credits"
          />
          <StatChip
            icon={<Users size={18} color="#60a5fa" />}
            label={t.comm_stat_referrals || "Successful referrals"}
            value={(totals.referrals || 0).toLocaleString()}
            testid="leaderboard-total-referrals"
          />
        </div>

        {/* Leaderboard list */}
        <div style={{ marginTop: 40 }}>
          {loading ? (
            <div style={{ color: "#8b8ba7", textAlign: "center", padding: 40 }}>
              {t.comm_loading || "Loading leaderboard…"}
            </div>
          ) : top.length === 0 ? (
            <div
              data-testid="leaderboard-empty"
              style={{
                padding: "40px 20px", textAlign: "center",
                background: "rgba(20,20,35,0.6)", borderRadius: 16,
                border: "1px dashed rgba(255,255,255,0.1)", color: "#a5a5c0",
              }}
            >
              <Trophy size={40} style={{ margin: "0 auto 12px", opacity: 0.4, display: "block" }} />
              <div style={{ fontWeight: 800, fontSize: 16, color: "#fff", marginBottom: 6 }}>
                {t.comm_empty_title || "Be the first on the leaderboard"}
              </div>
              <div style={{ fontSize: 14 }}>
                {t.comm_empty_sub || "Share your personal code below — every redeem earns you +10 credits."}
              </div>
            </div>
          ) : (
            <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}
              data-testid="leaderboard-list">
              {top.map((row) => <LeaderboardRow key={row.rank} row={row} t={t} />)}
            </ol>
          )}
        </div>

        {/* Hall of Fame — past monthly champions */}
        {hallOfFame.length > 0 && (
          <div style={{ marginTop: 48 }} data-testid="hall-of-fame">
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14 }}>
              <Trophy size={18} style={{ color: "#fde047" }} />
              <h2 style={{ color: "#fff", fontSize: 20, fontWeight: 900, margin: 0, letterSpacing: "-0.01em" }}>
                {t.comm_hof_title || "Hall of Fame"}
              </h2>
              <span style={{ color: "#8b8ba7", fontSize: 12 }}>{t.comm_hof_sub || "Past champions"}</span>
            </div>
            <div style={{ display: "grid", gap: 14 }}>
              {hallOfFame.map((period) => (
                <div key={period.period_key}
                  data-testid={`hof-period-${period.period_key}`}
                  style={{
                    padding: 16,
                    background: "rgba(20,20,35,0.55)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    borderRadius: 12,
                  }}>
                  <div style={{ color: "#c084fc", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>
                    {period.period_label}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 10 }}>
                    {period.winners.map((w) => {
                      const theme = RANK_THEME[w.rank];
                      const Icon = theme?.icon || Trophy;
                      return (
                        <div key={w.rank}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 12px",
                            background: "rgba(0,0,0,0.3)",
                            borderRadius: 10, border: "1px solid rgba(255,255,255,0.05)",
                          }}>
                          <div style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 34, height: 34, borderRadius: 8,
                            background: theme?.bg || "#334155", color: theme?.text || "#fff", flexShrink: 0,
                          }}>
                            <Icon size={16} />
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ color: "#fff", fontSize: 13, fontWeight: 800 }}>
                              {w.initials}<span style={{ color: "#8b8ba7", fontWeight: 500 }}>{w.suffix}</span>
                            </div>
                            <div style={{ color: "#4ade80", fontSize: 11, fontWeight: 700 }}>
                              +{w.prize_credits} {t.comm_hof_prize || "prize"} · {w.referrals} {t.comm_hof_refs || "referrals"}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Your own referral card — drives action */}
        {myRef && (
          <div style={{ marginTop: 40 }}>
            <div style={{ color: "#8b8ba7", fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>
              {t.comm_your_code || "Your code"}
            </div>
            <ReferralShareCard referral={myRef} />
          </div>
        )}

        {/* Fallback CTA for anon */}
        {!myRef && (
          <div
            style={{
              marginTop: 40, padding: 24, textAlign: "center",
              background: "linear-gradient(135deg,rgba(168,85,247,0.14),rgba(124,58,237,0.14))",
              border: "1px solid rgba(168,85,247,0.3)", borderRadius: 16,
            }}
          >
            <Gift size={30} style={{ margin: "0 auto 12px", color: "#c084fc", display: "block" }} />
            <div style={{ color: "#fff", fontSize: 18, fontWeight: 800, marginBottom: 6 }}>
              {t.comm_join_title || "Want in on this?"}
            </div>
            <div style={{ color: "#a5a5c0", fontSize: 14, marginBottom: 16 }}>
              {t.comm_join_sub || "Sign up to get your personal referral code + 3 starter credits."}
            </div>
            <Link
              to="/login"
              className="ads-btn ads-btn-primary"
              data-testid="leaderboard-signup-cta"
              style={{ padding: "12px 24px", fontSize: 15, display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              {t.comm_join_cta || "Join and share"} <ArrowRight size={16} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function StatChip({ icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        padding: 16,
        background: "rgba(20,20,35,0.7)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 12, textAlign: "center",
      }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#a5a5c0", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {icon} {label}
      </div>
      <div style={{ color: "#fff", fontSize: 28, fontWeight: 900, marginTop: 4, letterSpacing: "-0.02em" }}>
        {value}
      </div>
    </div>
  );
}

function LeaderboardRow({ row, t }) {
  const theme = RANK_THEME[row.rank];
  const Icon = theme?.icon;
  const bgAccent = theme?.bg || "linear-gradient(135deg,#334155 0%,#1e293b 100%)";
  const labelText = theme?.labelKey ? (t?.[theme.labelKey]) : null;
  return (
    <li
      data-testid={`leaderboard-row-${row.rank}`}
      style={{
        display: "flex", alignItems: "center", gap: 14,
        padding: "14px 18px",
        background: "rgba(20,20,35,0.7)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 14,
        boxShadow: theme?.glow,
      }}>
      <div style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 44, height: 44, borderRadius: 12,
        background: bgAccent, color: theme?.text || "#fff",
        fontWeight: 900, fontSize: 18, letterSpacing: "-0.02em", flexShrink: 0,
      }}>
        {theme ? <Icon size={20} /> : `#${row.rank}`}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: "#fff", fontSize: 16, fontWeight: 800, letterSpacing: "-0.01em" }}>
          {row.initials}<span style={{ color: "#8b8ba7", fontWeight: 500 }}>{row.suffix}</span>
        </div>
        {labelText && (
          <div style={{ color: "#8b8ba7", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 2 }}>
            {labelText}
          </div>
        )}
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ color: "#4ade80", fontSize: 20, fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
          +{row.credits.toLocaleString()}
        </div>
        <div style={{ color: "#8b8ba7", fontSize: 11 }}>
          {row.referrals.toLocaleString()} {row.referrals === 1 ? (t?.comm_referral_one || "referral") : (t?.comm_referral_many || "referrals")}
        </div>
      </div>
    </li>
  );
}
